import { useMutation, useQueryClient } from '@tanstack/react-query'
import { MarkerPin01 } from '@untitledui/icons'
import i18n from 'i18next'
import { useNavigate } from 'react-router-dom'

import { isSameDay } from '@/lib/utils'
import { Select } from '@components/designSystem/select/select'
import { useCurrentStore } from '@hooks/useCurrentStore.ts'
import { useScan } from '@hooks/useScan.ts'
import { addDayToLink, LATEST_DAY, useSelectedDay } from '@hooks/useSelectedDay'
import useToast from '@hooks/useToast.ts'
import useTypedTranslation from '@hooks/useTypedTranslation'
import { getScansQueryOptions, useScans } from '@state/scans.ts'
import { type Store, useStoreGrants } from '@state/store'
import { buildUrlWithReplacedStoreAndScan } from '@utils/routes.ts'

import type { Key } from 'react-aria-components'

/**
 * Custom hook to navigate to selected store.
 * * Uses `useMutation` to wrap the imperative `fetchQuery` call. This is done to
 * leverage the Mutation Lifecycle's guaranteed onSuccess/onError callbacks for robust, centralized side-effect management (navigation, toasts) and
 * automatic loading state tracking (`isPending`).
 */
function useNavigateToSelectedStore() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const currentScan = useScan()
  const { day } = useSelectedDay()
  const { showToast } = useToast()
  const { t } = useTypedTranslation()

  return useMutation({
    mutationFn: async (store: Store) => queryClient.query(getScansQueryOptions(store.id)),
    onSuccess: (scans, store) => {
      // stay in the day view; the selected date may not exist in the other store
      if (day) {
        navigate(addDayToLink(buildUrlWithReplacedStoreAndScan(store.id, 'latest'), LATEST_DAY))
        return
      }

      // if we currently are on the latest scan, just navigate to the latest scan of the selected store
      if (currentScan.isLatest) {
        navigate(buildUrlWithReplacedStoreAndScan(store.id, 'latest'))
        return
      }

      const currentScanDate = currentScan.scanDate
      const scanOnSameDay = scans.find(scan => isSameDay(scan.scanDate, currentScanDate))

      // if there is no scan on the same day, we automatically redirect to the latest scan
      if (!scanOnSameDay) {
        navigate(buildUrlWithReplacedStoreAndScan(store.id, 'latest'))
        showToast({
          variant: 'info',
          title: t('quickSelects.store.redirectedToLatestScan.title', { storeName: store.name }),
          description: t(
            'quickSelects.store.redirectedToLatestScan.description',
            {
              storeName: store.name,
              scanDate: currentScanDate.toLocaleDateString(i18n.language, { dateStyle: 'short' }),
            }
          ),
        })
        return
      }

      // there is a scan on the same day, navigate to it
      navigate(buildUrlWithReplacedStoreAndScan(store.id, scanOnSameDay.id))
    },
    onError: () => {
      showToast({
        variant: 'error',
        title: t('quickSelects.store.errorSwitchingStore.title'),
        description: t('quickSelects.store.errorSwitchingStore.description'),
      })
    },
  })
}

function QuickStoreSwitcher() {
  const { t } = useTypedTranslation()
  const { id: currentStoreId } = useCurrentStore()
  const { data: storeGrants } = useStoreGrants()
  const { mutate: navigateToSelectedStore, isPending } = useNavigateToSelectedStore()

  function handleStoreSelect(storeId: Key | null) {
    const selectedStore = storeGrants.find(grant => grant.store.id === storeId)
    if (!selectedStore) return // should not happen
    navigateToSelectedStore(selectedStore.store)
  }

  return (
    <Select
      size='sm'
      placeholderIcon={<MarkerPin01 className='text-brand-400' />}
      aria-label={t('quickSelects.store.name')}
      value={currentStoreId}
      onChange={handleStoreSelect}
      isDisabled={isPending}
    >
      {storeGrants.map(grant => <StoreSelectItem key={grant.store.id} store={grant.store} />)}
    </Select>
  )
}

function StoreSelectItem({ store }: {store: Store}) {
  const { t } = useTypedTranslation()
  const { data: selectedStoreScans } = useScans(store.id)

  const address = store.address ? `${store.address.addressLines[0]}, ${store.address.city}` : t('quickSelects.store.noAddress')

  const hasScans = selectedStoreScans && selectedStoreScans.length > 0

  return (
    <Select.Item id={store.id} supportingText={address} isDisabled={!hasScans}>
      {store.name}
    </Select.Item>
  )
}

export default QuickStoreSwitcher